import React, { useContext, useEffect, useRef, useState } from "react";
import { db, storage } from "../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "./CreatePost.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { MemberContext } from "../Contexts/MemberContext";

const CreatePost = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const textareaRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const {
    createstate,
    eventTitle,
    eventDate,
    eventDateWeek,
    eventParticipantMember,
  } = location.state || {};

  const [image, setImage] = useState(null);
  const [newParticipantMember] = useState(eventParticipantMember ?? "");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);

  /** ✅ MemberContext（1回だけ） */
  const { currentUser, currentUserInfo, membersLoading } =
    useContext(MemberContext);

  const postaccountname = currentUserInfo?.accountname ?? "";
  const postaccountId = currentUserInfo?.id ?? "";

  // -------------------------------------------------
  // 🔐 管理者のみ投稿可
  // -------------------------------------------------
  useEffect(() => {
    if (membersLoading) return;

    if (!currentUser) {
      alert("ログインしてください。");
      navigate("/login");
      return;
    }

    if (currentUserInfo?.administrator !== true) {
      alert("投稿は管理者のみ可能です。");
      navigate("/eventlist");
    }
  }, [membersLoading, currentUser, currentUserInfo, navigate]);

  // -------------------------------------------------
  // 🔥 textarea 自動リサイズ
  // -------------------------------------------------
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "20px";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [content]);

  // -------------------------------------------------
  // 🔥 イベント経由初期値
  // -------------------------------------------------
  useEffect(() => {
    if (eventTitle) {
      setTitle(`${eventDate}(${eventDateWeek}) ${eventTitle}`);
      setSelectedOptions(["event_report"]);
    }
  }, [eventTitle, eventDate, eventDateWeek]);

  const options = [
    { jp: "イベント報告", en: "event_report" },
    { jp: "会の情報", en: "member_exchange" },
    { jp: "テニス情報", en: "recommendations" },
    { jp: "よもやま話", en: "others" },
  ];

  const handleCheckboxChange = (e) => {
    setSelectedOptions([e.target.value]);
  };

  // -------------------------------------------------
  // 🔥 画像処理（元構造維持）
  // -------------------------------------------------
  const handleImageChange = (e) => {
    if (!e.target.files[0]) return;

    const reader = new FileReader();
    reader.readAsDataURL(e.target.files[0]);
    reader.onload = (ev) => {
      const img = new Image();
      img.src = ev.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const minSize = Math.min(img.width, img.height);
        const offsetX = (img.width - minSize) / 2;
        const offsetY = (img.height - minSize) / 2;

        canvas.width = 800;
        canvas.height = 800;
        ctx.drawImage(img, offsetX, offsetY, minSize, minSize, 0, 0, 800, 800);

        canvas.toBlob((blob) => setImage(blob), "image/jpeg", 0.7);
      };
    };
  };

  // -------------------------------------------------
  // 🔥 投稿処理
  // -------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedOptions.length === 0) {
      alert("分類を選んでください");
      return;
    }

    setLoading(true);

    let imageUrl = "";

    if (image) {
      const storageRef = ref(storage, `blog-images/${Date.now()}.jpg`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, async () => {
          imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve();
        });
      });
    }

    const section = options.reduce((acc, option) => {
      acc[option.en] = selectedOptions.includes(option.en);
      return acc;
    }, {});

    const trueKey = Object.entries(section).find(([, v]) => v)?.[0] ?? null;

    await addDoc(collection(db, "posts"), {
      section,
      title,
      content,
      participant: newParticipantMember,
      imageUrl,
      postaccountname,
      postaccountId,
      youtubeUrl: youtubeUrl ?? "",
      youtubeType:
        youtubeUrl && createstate === "eventblog" ? "practice" : "none",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    navigate("/postlist", { state: { createsection: trueKey } });
  };

  // -------------------------------------------------
  // 🔥 ローディング
  // -------------------------------------------------
  if (membersLoading) {
    return (
      <div className="createpost_container">
        <div className="createpost_content">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="createpost_container">
      <div className="createpost_content">
        <h2>
          <span className="createpost_content_title">新しい記事を投稿</span>
          <div
            className="createpost-close-button"
            onClick={() => navigate("/postlist")}
          >
            ｘ
          </div>
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="createpost_field">
            <h3>分 類</h3>
            <div className="createpost_field_inner">
              <div className="createpost_field_section">
                {options.map((option) => (
                  <label key={option.en} className="checkbox-label">
                    {option.jp}
                    <input
                      className="createpost_field_section_select"
                      type="checkbox"
                      value={option.en}
                      checked={selectedOptions.includes(option.en)}
                      onChange={handleCheckboxChange}
                    />
                  </label>
                ))}
              </div>
            </div>

            <h3>タイトル</h3>
            <div className="createpost_field_inner">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <h3>内 容</h3>
            <div className="createpost_field_inner2">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
              />
            </div>

            <h3>アップする写真</h3>
            <div className="createpost_field_inner">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>

            <h3>紹介するyoutube</h3>
            <div className="createpost_field_inner">
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="createpost_submit_button"
            >
              {loading ? "投稿中..." : "投稿"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePost;
